import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { useBreakpoints } from 'hooks';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { aiSections } from 'services/businessPlanAI/sections';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getAIAssistantReadinessPercent,
  getBusinessPlan,
  getBusinessPlanType,
} from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { AIGenerationErrorModalParent } from 'pages/BusinessToolPage/components/AIGenerationErrorModalParent/AIGenerationErrorModalParent';
import { useBusinessToolAiContext } from 'pages/BusinessToolPage/components/BusinessToolAiProvider';

import { ReactComponent as LockIcon } from 'assets/icons/LockIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useBusinessPlanAIFieldUpdate } from 'hooks/useBusinessPlanAIFieldUpdate';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { AIIconButton } from 'components/AIIconButton';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';
import ModalContext from 'components/ModalManager/modal-context';
import { SaveProjectModal } from 'components/SaveProjectModal/SaveProjectModal';

import { getValidatedMetadata } from 'helpers/metadataParser';

import { IFormHeaderProps } from './types';
import { Button } from '../Button/Button';

import styles from './FormHeader.module.scss';

import scssVariables from 'styles/sass-variables.module.scss';

export const FormHeader: React.FC<IFormHeaderProps> = ({ title, badgeText, locked, section, ...rest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthenticated();
  const { showModal, hideModal } = useContext(ModalContext);
  const { handleClickUpgradePlan } = useHeaderLogic();
  const { generateSectionAIImproved } = useBusinessToolAiContext();
  const { isMobile } = useBreakpoints();

  const { id: businessPlanId = '' } = useParams();
  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const updateMutation = useUpdateBusinessPlan();

  const AIAssistantReadinessPercent = useBusinessToolStore(getAIAssistantReadinessPercent);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);

  const isAIForChapterAvailable =
    businessPlanType === BUSINESS_PLAN_TYPES.FOR_BANK || businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const { handleApiRequest } = useBusinessPlanAIFieldUpdate(
    section ? aiSections[section] : aiSections.FOR_BANK_OVERVIEW,
    20,
    'text',
    false,
  );

  const preparedSectionName = section ? (section.includes('FOR_BANK') ? section.slice(9) : section.slice(14)) : '';

  const isSectionAlreadyGenerated = businessPlan.metadata.aiGeneratedSections
    ? businessPlan.metadata.aiGeneratedSections.includes(preparedSectionName)
    : false;

  const handleAiSectionClick = () => {
    if (!isAuthenticated) {
      showModal(SaveProjectModal, {
        onClose: hideModal,
        title: t('pleaseLogin'),
        description: t('loginForAIGeneration'),
      });
    } else if (isSectionAlreadyGenerated && !isStandardOrLightTariff) {
      return;
    } else if (section) {
      generateSectionAIImproved(section, async () => {
        await handleApiRequest('generate', true);
        if (section.includes('FOR_BANK') || section.includes('FOR_INVESTORS')) {
          const params = {
            id: businessPlanId,
            body: {
              ...businessPlan,
              metadata: JSON.stringify({
                ...getValidatedMetadata(businessPlan.metadata),
                aiGeneratedSections: [...businessPlan.metadata.aiGeneratedSections, preparedSectionName],
              }),
            },
          };
          updateMutation.mutateAsync(params);
        }
      });
    }
  };

  const handleAiIconButonWrapperClick = () => {
    if (isMobile && AIAssistantReadinessPercent < 100) {
      showModal(AIGenerationErrorModalParent, { close: hideModal, errorMessage: 'NOT_ENOUGH_CONTEXT' });
    }
  };

  return (
    <Box {...rest}>
      <Box className={styles.titleContainer} sx={{ marginBottom: badgeText ? 2 : 0 }}>
        <Box className={styles.titleIconContainer}>
          <Typography variant="h3">{title}</Typography>
          {locked && (
            <Box className={styles.iconContainer}>
              <LockIcon />
            </Box>
          )}
        </Box>
        {locked && (
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            className={styles.upgradeButton}
            onClick={handleClickUpgradePlan}
          >
            {t('upgradePlan')}
          </Button>
        )}
        {!locked && section && isAIForChapterAvailable && (
          <Box minHeight="30px" onClick={handleAiIconButonWrapperClick}>
            <AIIconButton
              disabled={AIAssistantReadinessPercent < 100}
              onClick={handleAiSectionClick}
              isUpgradeNeeded={!isStandardOrLightTariff && isSectionAlreadyGenerated}
            />
          </Box>
        )}
      </Box>

      {badgeText && (
        <Typography
          variant="h6"
          sx={{ backgroundColor: locked ? scssVariables.greys_background : scssVariables.green_100 }}
          className={clsx(styles.badge, { [styles.disabled]: locked })}
        >
          {badgeText}
        </Typography>
      )}
    </Box>
  );
};
